import { useEffect, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Stack, MenuItem, Alert,
  Table, TableHead, TableRow, TableCell, TableBody, IconButton, Typography,
} from '@mui/material';
import { Add, Delete } from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { evcpdProductsApi } from '../../../api/evcpdProducts.api';
import type { CreatePlanInput } from '../../../api/production.api';

interface Props {
  open: boolean;
  onClose: () => void;
  onSubmit: (input: CreatePlanInput) => Promise<void>;
  error?: string | null;
}

interface Line { productId: number; plannedQty: number }

export function ProductionPlanFormDialog({ open, onClose, onSubmit, error }: Props) {
  const [title, setTitle] = useState('');
  const [periodStart, setPeriodStart] = useState('');
  const [periodEnd, setPeriodEnd] = useState('');
  const [lines, setLines] = useState<Line[]>([]);
  const [productId, setProductId] = useState<number | ''>('');
  const [qty, setQty] = useState('');

  const products = useQuery({ queryKey: ['evcpd-products', 'plan-picker'], queryFn: () => evcpdProductsApi.list({ status: 'active', limit: 200 }), enabled: open });

  useEffect(() => {
    if (!open) { setTitle(''); setPeriodStart(''); setPeriodEnd(''); setLines([]); setProductId(''); setQty(''); }
  }, [open]);

  const productName = (id: number) => products.data?.data.find((p) => p.id === id)?.name ?? `#${id}`;
  const addLine = () => {
    if (!productId || !Number(qty)) return;
    setLines((ls) => [...ls.filter((l) => l.productId !== productId), { productId: Number(productId), plannedQty: Number(qty) }]);
    setProductId(''); setQty('');
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>New Production Plan</DialogTitle>
      <DialogContent>
        <Stack spacing={2} mt={1}>
          {error && <Alert severity="error">{error}</Alert>}
          <TextField label="Title" value={title} onChange={(e) => setTitle(e.target.value)} required fullWidth />
          <Stack direction="row" spacing={2}>
            <TextField label="Period Start" type="date" InputLabelProps={{ shrink: true }} value={periodStart} onChange={(e) => setPeriodStart(e.target.value)} fullWidth />
            <TextField label="Period End" type="date" InputLabelProps={{ shrink: true }} value={periodEnd} onChange={(e) => setPeriodEnd(e.target.value)} fullWidth />
          </Stack>
          <Typography variant="subtitle2">Planned products</Typography>
          <Stack direction="row" spacing={2} alignItems="center">
            <TextField select size="small" label="Product" value={productId} onChange={(e) => setProductId(Number(e.target.value))} sx={{ minWidth: 240 }}>
              {(products.data?.data ?? []).map((p) => <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>)}
            </TextField>
            <TextField size="small" label="Planned Qty" type="number" value={qty} onChange={(e) => setQty(e.target.value)} sx={{ width: 130 }} />
            <Button startIcon={<Add />} onClick={addLine} disabled={!productId || !Number(qty)}>Add</Button>
          </Stack>
          <Table size="small">
            <TableHead><TableRow><TableCell>Product</TableCell><TableCell align="right">Planned Qty</TableCell><TableCell /></TableRow></TableHead>
            <TableBody>
              {lines.map((l) => (
                <TableRow key={l.productId}>
                  <TableCell>{productName(l.productId)}</TableCell>
                  <TableCell align="right">{l.plannedQty}</TableCell>
                  <TableCell align="right"><IconButton size="small" color="error" onClick={() => setLines((ls) => ls.filter((x) => x.productId !== l.productId))}><Delete fontSize="small" /></IconButton></TableCell>
                </TableRow>
              ))}
              {lines.length === 0 && <TableRow><TableCell colSpan={3}>No products added.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          disabled={!title || lines.length === 0}
          onClick={() => onSubmit({ title, periodStart: periodStart || undefined, periodEnd: periodEnd || undefined, items: lines })}
        >
          Create
        </Button>
      </DialogActions>
    </Dialog>
  );
}
